import React, { useEffect, useMemo, useRef, useState } from 'react';

import Formsy from 'formsy-react';
import { Tabs, TabsList, TabsTrigger, Row, Col, Label, FormGroup, Button, Badge, TabsContent, FormSection, Textarea, Select, SimpleAlert, Input, SheetBody, SheetFooter, Separator } from 'twyne-new.ui';

import InfoTip from '@/components/InfoTip';
import Summary from '@/modules/report/views/losses/components/Summary';
import { useAppAlert } from '@Components/AppAlert';
import SelectForm from '@Components/form/Select';
import { RemoteTable } from '@Components/table';
import { formatDateTime } from '@Utils/index';
import Inputy from 'components/form/Input';
import sweepWinnerStore from 'modules/report/stores/sweepWinner';

function WinnerTab() {
  const { handleSuccess, handleError } = useAppAlert()
  const [state, setState] = useState()
  const [status, setStatus] = useState()

  const userSession = state?.sweepSubscription?.userSession
  const user = userSession?.user

  useEffect(() => {
    loadData()
  }, [])

  useEffect(() => {
    if (status === undefined && state?.winnerStatus) {
      setStatus(state.winnerStatus)
    }
  }, [status, state?.winnerStatus])

  function loadData() {
    setState(sweepWinnerStore.collection.winner)
  }

  const allowNewWinner = useMemo(() => status !== 'forfeited' && state?.winnerStatus === 'forfeited' && state?.drawType === 'sweepstake', [status, state?.winnerStatus, state?.drawType])

  const winnerStatusOptions = [
    { label: 'Fulfilled', value: 'fulfilled' },
    { label: 'Pending Contact', value: 'pending_contact' },
    { label: 'Pending Verification', value: 'pending_verification' },
    { label: 'Forfeited', value: 'forfeited' },
  ]

  const verificationFields = [
    {
      field: 'emailVerification',
      label: 'Email',
      labelValid: 'Valid',
      labelInvalid: 'Not valid',
    },
    {
      field: 'addressVerification',
      label: 'Address',
      labelValid: 'Verified',
      labelInvalid: 'Not verified',
    },
    {
      field: 'botVerification',
      label: 'Bot',
      labelValid: 'Verified',
      labelInvalid: 'Not verified',
    },
    {
      field: 'fraudVerification',
      label: 'Fraud',
      labelValid: 'Not fraud',
      labelInvalid: 'fraud',
    },
    {
      field: 'phoneVerification',
      label: 'Phone',
      labelValid: 'Verified',
      labelInvalid: 'Not verified',
    },
    {
      field: 'postalVerification',
      label: 'Postal',
      labelValid: 'Valid',
      labelInvalid: 'Not valid',
    },
  ]

  function handleChangeWinnerStatus(winnerStatusSelected) {
    const newState = { ...state, winnerStatus: winnerStatusSelected.value }
    setState(newState);
  }

  async function handleGenerateNewWinner() {
    try {
      await sweepWinnerStore.generateNewWinner({ sweepContestWinnerId: state.id })
      handleSuccess('Success', 'Winner status edited and new winner generated.');
    } catch (error) {
      handleError('Error', 'Winner was not generated.');
    }
  }

  function submit() {
    sweepWinnerStore.updateWinnerStatus({
      sweepContestWinnerId: state.id,
      winnerStatus: state.winnerStatus,
    }).then(() => {
      if (!allowNewWinner) {
        return handleSuccess('Success', 'Winner status edited.');
      }

      handleGenerateNewWinner()
    }).catch(() => {
      handleError('Error', 'Winner status was not edited.');
    });
  }

  if (!user) {
    return null
  }

  return <>
    <SheetBody>
      <Row>
        <Col>
          <FormSection>
            User
          </FormSection>
        </Col>
        <Col size="4">
          <FormGroup>
            <Label>First Name</Label>
            <Input value={user.first_name ?? ''} disabled />
          </FormGroup>
        </Col>
        <Col size="4">
          <FormGroup>
            <Label>Last Name</Label>
            <Input value={user.last_name ?? ''} disabled />
          </FormGroup>
        </Col>
        <Col size="4">
          <FormGroup>
            <Label>Email</Label>
            <Input value={user.email ?? ''} disabled />
          </FormGroup>
        </Col>
        <Col size="4">
          <FormGroup>
            <Label>Address</Label>
            <Input value={user.address1 ?? ''} disabled />
          </FormGroup>
        </Col>
        <Col size="4">
          <FormGroup>
            <Label>Address 2</Label>
            <Input value={user.address2 ?? ''} disabled />
          </FormGroup>
        </Col>
        <Col size="4">
          <FormGroup>
            <Label>Phone</Label>
            <Input value={user.phone ?? ''} disabled />
          </FormGroup>
        </Col>
        <Col size="4">
          <FormGroup>
            <Label>City</Label>
            <Input value={user.city ?? ''} disabled />
          </FormGroup>
        </Col>
        <Col size="4">
          <FormGroup>
            <Label>State</Label>
            <Input value={user.state ?? ''} disabled />
          </FormGroup>
        </Col>
        <Col size="4">
          <FormGroup>
            <Label>ZIP</Label>
            <Input value={user.zip ?? ''} disabled />
          </FormGroup>
        </Col>

        <Col size="12">
          <Separator />
        </Col>

        <Col size="12">
          <FormSection>
            Verifications
          </FormSection>
        </Col>

        {verificationFields.map(item => {
          const color = userSession[item.field] == 1 ? 'success' : 'danger'
          const badgeDescription = userSession[item.field] == 1 ? item.labelValid : item.labelInvalid
          return <Col size="4" key={item.field}>
            <div className="flex flex-col gap-1">
              <span>{item.label}</span>
              <Badge variant='light' color={color}>
                {badgeDescription}
              </Badge>
            </div>
          </Col>
        })}

        <Col size="6">
          <FormGroup>
            <Label>
              Winner Status
            </Label>
            <Select
              value={state.winnerStatus}
              options={winnerStatusOptions}
              onChange={handleChangeWinnerStatus}
              required
            />
          </FormGroup>
        </Col>

        {allowNewWinner && <>
          <Col size="12">
            <SimpleAlert variant="warning">
              Changing the status to forfeited will <strong>automatically select a new winner.</strong>
            </SimpleAlert>
          </Col>
        </>}

      </Row>
    </SheetBody>
    <SheetFooter>
      <Button color="accent" onClick={submit}>
        Apply
      </Button>
    </SheetFooter>
  </>
}

function TicketsTab() {
  const columns = [
    {
      dataField: 'ticket',
      sort: false,
      text: 'ticket',
    },
    {
      dataField: 'createdAt',
      formatter: formatDateTime,
      sort: false,
      text: 'Date Time',
    },
  ]

  const ticketsData = sweepWinnerStore.collection.tickets
  const ticketsCount = ticketsData && ticketsData.length

  return (
    <>
      <Row>
        <Col size="12">
          <Summary
            className='p-0 border twy-border-base'
            key='summary'
            cards={[{
              title: "Total Tickets",
              get value() {
                return ticketsCount
              },
            }]}
            isLoading={false}
          />
        </Col>
        <Col size="12" className='overflow-x-auto max-h-[500px]'>
          <RemoteTable columns={columns} data={ticketsData} margins={true} />
        </Col>
      </Row>
    </>
  )
}

function ContactsTab() {
  const { handleSuccess, handleError } = useAppAlert()
  const [state, setState] = useState()
  const formRef = useRef(null)

  useEffect(() => {
    loadData()
  }, [])

  function loadData() {
    const newState = {
      data: sweepWinnerStore.collection.contacts.data,
      sweepContestWinnerId: sweepWinnerStore.collection.contacts.contestWinnerId,
    }

    setState(newState)
  }

  const typeOptions = [
    { label: 'Phone', value: 'phone' },
    { label: 'Email', value: 'email' },
  ]

  const resultTypeOptions = [
    { label: 'No answer', value: 'no_answer' },
    { label: 'Wrong number', value: 'wrong_number' },
    { label: 'Left Voicemail', value: 'left_voicemail' },
    { label: 'Disconnected', value: 'disconnected' },
    { label: 'Contacted', value: 'contacted' },
  ]

  const columns = [
    {
      dataField: 'contactType',
      sort: false,
      text: 'Contact Type',
      formatter: contactTypeFormatter,
    },
    {
      dataField: 'resultType',
      sort: false,
      text: 'Result Type',
      formatter: resultTypeFormatter,
    },
    {
      dataField: 'createdAt',
      formatter: formatDateTime,
      sort: false,
      text: 'Date',
    },
    {
      dataField: 'note',
      formatter: noteFormatter,
      sort: false,
      text: 'Note',
    },
  ]

  function contactTypeFormatter(_, row) {
    return typeOptions.find(type => type.value === row.contactType).label
  }

  function noteFormatter(_, row) {
    if (!row?.note) {
      return '--'
    }

    return <InfoTip id={`info-note-${row.id}`}>
      {row?.note}
    </InfoTip>
  }

  function resultTypeFormatter(_, row) {
    return resultTypeOptions.find(type => type.value === row.resultType).label
  }

  function handleValidSubmit(model) {
    const { sweepContestWinnerId } = state
    const { contactType, resultType, note } = model
    sweepWinnerStore.createWinnerContact({
      contactType,
      resultType,
      note,
      sweepContestWinnerId,
    }).then(() => {
      handleSuccess('Success', 'Winner contact created.');
      loadData()
      if (formRef.current) {
        formRef.current.reset()
      }
    }).catch(() => {
      handleError('Error', 'Winner contact was not created.');
    });
  }

  return (
    <>
      {state && <>
        <Formsy ref={formRef} onValidSubmit={handleValidSubmit}>
          <Row>
            <Col size="4">
              <FormGroup>
                <Label>
                  Type
                </Label>
                <SelectForm
                  name="contactType"
                  required
                  clearable={false}
                  options={typeOptions}
                />
              </FormGroup>
            </Col>
            <Col size="4">
              <FormGroup>
                <Label>
                  Result Type
                </Label>
                <SelectForm
                  name="resultType"
                  required
                  clearable={false}
                  options={resultTypeOptions}
                />
              </FormGroup>
            </Col>
            <Col size="4">
              <FormGroup>
                <Label>Date</Label>
                <Inputy
                  name="date"
                  value={formatDateTime(new Date())}
                  disabled
                />
              </FormGroup>
            </Col>

            <Col size="12">
              <FormGroup>
                <Label>Notes</Label>
                <Inputy
                  component={Textarea}
                  name="note"
                  type="Textarea"
                  rows="3"
                />
              </FormGroup>
            </Col>

            <Col size="12" className='flex justify-end'>
              <FormGroup>
                <Button className='w-fit' type="submit" color="accent">
                  Apply
                </Button>
              </FormGroup>
            </Col>
            <Col>
              <Separator />
            </Col>
            <Col>
              <FormSection>
                Contacts history
              </FormSection>
            </Col>
            <Col size="12" className='overflow-x-auto max-h-[500px]'>
              <RemoteTable columns={columns} data={state.data} margins={true} />
            </Col>
          </Row>
        </Formsy>
      </>}
    </>
  )
}

function TabsWinnerSidebar() {
  return <>
    <Tabs defaultValue='winner'>
      <TabsList className='px-4'>
        <TabsTrigger value='winner'>Winner</TabsTrigger>
        <TabsTrigger value='tickets'>Tickets</TabsTrigger>
        <TabsTrigger value='contacts'>Contacts</TabsTrigger>
      </TabsList>
      <TabsContent value='winner' className='mt-0'>
        <WinnerTab />
      </TabsContent>
      <TabsContent value='tickets' className='mt-0'>
        <SheetBody>
          <TicketsTab />
        </SheetBody>
      </TabsContent>
      <TabsContent value='contacts' className='mt-0'>
        <SheetBody>
          <ContactsTab />
        </SheetBody>
      </TabsContent>
    </Tabs>
  </>
}

export default TabsWinnerSidebar
